export const OPTION_NAME_MAPPINGS = {
  color: ["color", "colour", "couleur"],
  artwork: ["artwork", "oeuvre"],
  style: ["style"],
  amount: ["amount"],
} as const;

export type CanonicalOptionName = keyof typeof OPTION_NAME_MAPPINGS;

/**
 * Maps a localized option name to its canonical one
 * @param optionName - The option name to map (case-insensitive)
 * @returns The canonical option name or null
 */
export const getCanonicalOptionName = (
  optionName: string
): CanonicalOptionName | null => {
  const normalizedOptionName = optionName.toLowerCase().trim();

  for (const [canonicalName, localizedNames] of Object.entries(
    OPTION_NAME_MAPPINGS
  )) {
    if (
      localizedNames.some(
        (localizedName) => normalizedOptionName === localizedName
      )
    ) {
      return canonicalName as CanonicalOptionName;
    }
  }
  return null;
};

/**
 * Checks if an option name matches a specific canonical name
 * @param localizedOptionName
 * @param canonicalOptionName
 * @returns True if the option name matches
 */
export const isCanonicalOptionName = (
  localizedOptionName: string,
  canonicalOptionName: CanonicalOptionName
): boolean => {
  return getCanonicalOptionName(localizedOptionName) === canonicalOptionName;
};

/**
 * Gets all localized names for a given canonical option name
 * @param canonicalOptionName - The canonical option name
 * @returns Array of localized names for the given option name
 */
export const getLocalizedOptionNames = (
  canonicalOptionName: CanonicalOptionName
): readonly string[] => {
  return OPTION_NAME_MAPPINGS[canonicalOptionName];
};
